<template>
    <UserPageLayout>
        <div class="pb-12">
            <div v-html="content" ref="content" class="content max-w-lg mx-auto system-text-color"></div>
            <div v-if="isCreator" class="max-w-lg mx-auto">
                <div class="mt-8 system-text-color">
                    <div v-if="userRole">
                        <p v-if="isAppliedToCreator" class="text-center font-bold mb-4">
                            {{ userRole == 'creator' ? 'Your are creator' : 'Apply to Creator' }}
                        </p>

                        <div v-if="isAppliedToCreator" class="text-center">
                            <a @click.prevent="clickNewQuiz" class="btn text-lg font-opensans">New Quiz</a>
                        </div>

                        <button
                            v-if="userRole == 'member'"
                            @click="applyToCreator"
                            class="btn btn-block text-lg font-opensans"
                        >
                            {{ userRole == 'creator' ? 'Your are creator' : 'Apply to Creator' }}
                        </button>

                        <button
                            v-if="['member', 'creator', 'premium_user'].indexOf(userRole) == -1"
                            class="btn btn-block text-lg font-opensans"
                            disabled
                        >
                            Only member can apply to creator
                        </button>
                    </div>
                    <div v-else>
                        <!-- If not logged in -->
                        <button class="btn btn-block text-lg font-opensans" @click="applyToCreator">
                            Apply to Creator
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <AuthPopup
            v-if="showAuthPopup && (isCreator || isContributor)"
            @login-success="loginSuccess"
            @register-success="loginSuccess"
            @close="showAuthPopup = false"
            :webview-redirect-to="createWebviewRedirectTo()"
        />
        <ContributorForm v-if="contentReady" @required-login="showAuthPopup = true" />
    </UserPageLayout>
</template>

<script>
import Request from '@/lib/request.js'
import appConfig from '@/app.config.js'
import UserPageLayout from '@/layouts/UserPage'
import popup from '@/lib/popup'
import { currentUser, isLoggedIn } from '@/lib/user'
import AuthPopup from '@/components/AuthPopup'
import ContributorForm from '@/components/ContributorForm'
import AuthPopupMixin from '@/mixins/auth-popup'

export default {
    name: 'page',

    metaInfo() {
        return {
            title: this.title,
            link: [
                {
                    rel: 'canonical',
                    href: this.canonicalUrl
                }
            ],
            meta: [
                {
                    name: 'description',
                    content: appConfig.meta.desc
                },
                {
                    name: 'keywords',
                    content: appConfig.meta.keywords
                }
            ]
        }
    },

    components: {
        UserPageLayout,
        AuthPopup,
        ContributorForm
    },

    mixins: [AuthPopupMixin],

    watch: {
        content(htmlText) {
            const slug = this.$route.params.slug,
                self = this
            if (htmlText.match(/<a href="#modal" (.*?)<[/]a>/g)) {
                var targetElem = htmlText.match(/<a href="#modal" (.*?)<[/]a>/g)
                targetElem.forEach(function (text) {
                    var modifyText = text.replace(/target="_blank"/, '').replace(/href="#modal"/, `href="#"`)
                    var modifyTextID = text
                        .replace(/target="_blank"/, '')
                        .replace(/href="#modal"/, `id="${slug}-trigger" href="#"`)
                    if (!htmlText.match(new RegExp(`<a id="${slug}-trigger" (.*?)</a>`, 'g'))) {
                        self.content = self.content.replace(text, modifyTextID)
                    } else {
                        self.content = self.content.replace(text, modifyText)
                    }
                })
            } else {
                this.contentReady = true
            }
        }
    },

    computed: {
        isCreator() {
            return this.$route.params.slug === 'creators'
        },

        isContributor() {
            return this.$route.params.slug === 'contributors'
        },

        isAppliedToCreator() {
            return ['premium_user', 'creator'].indexOf(this.userRole) > -1
        },

        canonicalUrl() {
            return `${process.env.VUE_APP_ROOT_URL}article/${this.$route.params.slug}`
        }
    },

    data() {
        return {
            isLoading: false,
            contentReady: false,
            content: '',
            title: '',
            userRole: currentUser().role
        }
    },

    mounted() {
        document.body.style.overflow = 'auto'
        this.fetchPageBySlug()
        this.handleHashChange()
    },

    methods: {
        fetchPageBySlug() {
            const request = new Request()
            const url = appConfig.api.fetchPageBySlug

            request
                .get(url, {
                    params: {
                        slug: this.$route.params.slug
                    }
                })
                .then(res => {
                    const page = res.data.data
                    this.content = page.content
                    this.title = page.title
                    this.handleOpenSlideLink()
                    this.handleAnchor()
                    this.resetToWhite()
                })
                .catch(() => {
                    window.location.href = '/'
                })
        },

        afterLoginSuccess() {
            this.userRole = currentUser().role
        },

        clickNewQuiz() {
            window.location.href = this.$router.resolve({ name: 'setting' }).href
        },

        applyToCreator() {
            if (!isLoggedIn()) {
                this.showAuthPopup = true

                return
            }

            this.$store.commit('toggleMainLoader', true)
            this.$store
                .dispatch('applyToCreator')
                .then(() => {
                    this.$store.dispatch('refreshToken').then(() => {
                        this.$store.commit('toggleMainLoader', false)
                        popup.alert(
                            'Thank you! \n You are now activated as a quiz creator for TheBigQuiz. \n You can now start creating and submit for approval',
                            () => {},
                            {
                                icon: 'success'
                            }
                        )

                        this.userRole = currentUser().role
                    })
                })
                .catch(() => {
                    popup.alert('Apply to quiz creator fail', () => {}, {
                        icon: 'error'
                    })

                    this.$store.commit('toggleMainLoader', false)
                })
        },

        handleOpenSlideLink() {
            this.$nextTick(() => {
                var links = document.querySelectorAll('a[href*="-slide"]')
                links.forEach(el => {
                    el.addEventListener('click', e => {
                        e.preventDefault()
                        var slug = e.target.getAttribute('href')
                        slug = slug.replace(/\#/, '').replace(/\-slide/, '')

                        var openPageEvent = new CustomEvent('page-open', {
                            detail: {
                                slug: slug
                            }
                        })

                        window.dispatchEvent(openPageEvent)
                    })
                })
            })
        },

        handleAnchor() {
            this.$nextTick(() => {
                this.$refs.content.querySelectorAll('a[href^="#"]').forEach(a => a.removeAttribute('target'))
                this.$refs.content.querySelectorAll('.ql-anchor-block').forEach(el => (el.innerText = ''))
            })
        },

        resetToWhite() {
            this.$nextTick(() => {
                this.$refs.content.querySelectorAll('span,strong,a').forEach(span => {
                    span.style.color = '#fff'
                })
            })
        },

        handleHashChange() {
            window.addEventListener('hashchange', () => {
                if (!location.hash) {
                    document.querySelector('#app').scrollTo(0, 0)
                } else {
                    document.querySelector(location.hash).scrollIntoView()
                }

                setTimeout(() => {
                    document.querySelector('.nav-header').classList.add('hide')
                }, 10)
            })
        }
    }
}
</script>

<style lang="scss">
.content {
    h1 {
        font-size: 1.875rem;
    }

    a {
        color: white;
    }

    blockquote {
        border-left: 0.7rem solid white;
        margin: 1.5em 0.7rem;
        padding: 0.5em 0.7rem;
    }

    hr {
        border-top: 0px;
        border-left: 0px;
        border-right: 0px;
        border-bottom: 1px solid #fff;
    }

    .ql-align-center {
        text-align: center;
    }
    .ql-align-right {
        text-align: right;
    }
    .ql-align-justify {
        text-align: justify;
    }

    .ql-video {
        &.ql-align-center {
            margin: 0 auto;
            display: block;
        }
        &.ql-align-right {
            margin-left: auto;
            display: block;
        }
    }

    .img-fluid {
        max-width: 100%;
        cursor: default !important;
    }

    img,
    .ql-video {
        max-width: 100%;
    }
}
</style>
